'use strict';

import { ref } from 'vue';
import { defineStore } from 'pinia';
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  reauthenticateWithCredential,
  sendEmailVerification,
  sendPasswordResetEmail,
  signOut as signoutOfFirebase,
  updateProfile,
  updatePassword,
  EmailAuthProvider,
} from 'firebase/auth';
import { getCurrentUser } from 'vuefire';
import { useFetch } from '../../app/composables/fetch';
import { camelCase, snakeCase } from '../../app/utils/change-case';
import { load } from 'recaptcha-v3';

/**
 * Pinia store for a single user
 */
export const useAuthStore = defineStore('auth', () => {
  /**
   * Auth user
   */
  const authUser = ref(null);

  /**
   * Loading
   */
  const loading = ref(false);

  /**
   * Submitting
   */
  const submitting = ref(false);

  /**
   * Error
   */
  const error = ref(null);

  /**
   * Get user by id
   * @param {Number} id - id of user
   * @returns {Void}
   */
  const getById = async (id) => {
    // Reset error
    error.value = null;

    // Call api
    loading.value = true;
    const { data, error: fetchError } = await useFetch(`account/${id}`)
      .get()
      .json();
    loading.value = false;

    // Exit if error
    if (fetchError.value) {
      error.value = fetchError.value;
      return;
    }

    // Change keys to camel case
    authUser.value = camelCase(data.value.data);
  };

  /**
   * Get user by firebase token
   * @returns {Void}
   */
  const getFirebaseUser = async () => {
    loading.value = true;

    authUser.value = await getCurrentUser();

    if (authUser.value) {
      authUser.value.role = (
        await authUser.value.getIdTokenResult()
      ).claims.role;
    }

    loading.value = false;

    return Promise.resolve(authUser.value);
  };

  /**
   * Add user
   * @returns {Promise}
   */
  const signup = async () => {
    submitting.value = true;

    const recaptcha = await load('6LeeCr0rAAAAAI08yEk4s_2MO7KE9xzSgAbaIbND', {
      useRecaptchaNet: true,
      autoHideBadge: true,
    });
    const token = await recaptcha.execute('signup');

    //console.log(token);
    authUser.value.recaptchaToken = token;

    // Create firebase user
    const userCredential = await createUserWithEmailAndPassword(
      getAuth(),
      authUser.value?.email,
      authUser.value?.password,
    );
    console.log('Account created in firebase');
    //console.log('Email verified?', userCredential.user.emailVerified)

    // Send email verification
    await sendEmailVerification(userCredential.user);

    // Set firebase id
    authUser.value.firebaseId = userCredential.user.uid;
    // Clear password from memory
    authUser.value.password = '';

    // Set display name for firebase user
    await updateProfile(userCredential.user, {
      displayName: authUser.value.firstName + ' ' + authUser.value.lastName,
    });

    // Change keys to snake case
    const payload = snakeCase(authUser.value);

    const { data, error: fetchError } = await useFetch('signup')
      .post(payload)
      .json();

    // Refresh id token to get updated claim which is the users role.
    await userCredential.user.getIdToken(true);

    submitting.value = false;

    // Exit if error
    if (fetchError.value) {
      error.value = fetchError.value;
      //throw error.value;
      return;
    }

    return Promise.resolve(data.value);
  };

  /**
   * Edit user
   * @param {Obejct} user - user object
   * @returns {Promise} user- simple object of user
   */
  const edit = async () => {
    // Reset error
    error.value = null;

    // Change keys to snake case
    const payload = mapKeys(authUser.value, (value, key) => {
      return snakeCase(key);
    });

    // Call api
    submitting.value = true;
    const { data, error: fetchError } = await useFetch(
      `users/${authUser.value.id}`,
    )
      .patch(payload)
      .json();
    submitting.value = false;

    // Exit if error
    if (fetchError.value) {
      error.value = fetchError.value;
      throw error.value;
    }

    // Change keys to camel case
    authUser.value = camelCase(data.value.data);

    return Promise.resolve(authUser.value);
  };

  /**
   * Signin to firebase
   * @returns {Promise}
   */
  const signin = async () => {
    // Reset error
    error.value = null;

    try {
      const userCredential = await signInWithEmailAndPassword(
        getAuth(),
        authUser.value?.email,
        authUser.value?.password,
      );

      // Set firebase id
      authUser.value.firebaseId = userCredential.user.uid;
    } catch (e) {
      error.value = e;
      return Promise.reject(e);
    }

    // Clear password from memory
    authUser.value.password = '';

    return Promise.resolve(true);
  };

  /**
   * Signout of firebase
   * @returns {Promise}
   */
  const signout = async () => {
    try {
      await signoutOfFirebase(getAuth());

      // Remove data from user ref
      authUser.value = null;

      return Promise.resolve(true);
    } catch (e) {
      return Promise.reject(e);
    }
  };

  /**
   * Change password in firebase
   * @returns {Promise}
   */
  const changePassword = async (currentPassword, newPassword) => {
    // Get auth credential to reauthenticate with
    const userCredential = EmailAuthProvider.credential(
      getAuth().currentUser.email,
      currentPassword,
    );

    // Reauthenticate to prevent no recent auth error
    await reauthenticateWithCredential(getAuth().currentUser, userCredential);

    // Update password
    await updatePassword(getAuth().currentUser, newPassword);

    return Promise.resolve(true);
  };

  /**
   * Forgot password reset email
   * @returns {Promise}
   */
  const forgotPassword = async (email) => {
    // Send password reset
    await sendPasswordResetEmail(getAuth(), email);

    return Promise.resolve(true);
  };

  /**
   * Update role
   * @returns {Promise}
   */
  const updateRole = async () => {
    // Reset error
    error.value = null;

    // Call api
    submitting.value = true;
    const { data, error: fetchError } = await useFetch(`users/organizer`)
      .post()
      .json();
    submitting.value = false;

    // Exit if error
    if (fetchError.value) {
      error.value = fetchError.value;
      //throw error.value;
      return;
    }

    // Refresh id token to get updated claim which is the users role.
    await (await getCurrentUser()).getIdToken(true);

    // Change keys to camel case
    authUser.value = camelCase(data.value.data);

    return Promise.resolve(authUser.value);
  };

  return {
    authUser,
    error,
    loading,
    submitting,
    getById,
    getFirebaseUser,
    signup,
    edit,
    signin,
    signout,
    changePassword,
    forgotPassword,
    updateRole,
  };
});
